import { Tooltip } from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IconButtonText } from 'common/components/IconButtonText';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { useNotifications } from 'modules/app/notifications';
import { SfsUkRevealPaymentPasswordButton } from 'modules/physical-cards/my-card/components/PlasticCard/SfsUkRevealPaymentPasswordButton/SfsUkRevealPaymentPasswordButton';
import {
  type CardBlockingReasonAccess,
  getCardAccess,
} from 'modules/physical-cards/utils/cardBlockingReasons';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { MarqetaSetPinModal } from './MarqetaSetPin';
import { SFSRevealPinButton } from './SFSRevealPin/SFSRevealPinButton';
import { type ApiCard } from '../../../card';
import {
  BlockUnblock,
  CardReport,
  TopUpModal,
} from '../../../components/CardActions';
import { doCardAction, getIsCardBlocked } from '../../../utils/card';

const TOPUP_MODAL_QUERY = 'topupModal';

type Props = {
  card: ApiCard;
  syncActivity: () => void;
  isCardBeingBlockedOrUnblocked: boolean;
  setIsCardBeingBlockedOrUnblocked: (isBlocking: boolean) => void;
};

function computeTopUpButtonState(
  card: ApiCard,
  cardsAccess: CardBlockingReasonAccess,
  isCardBeingBlockedOrUnblocked: boolean,
  isNonActivatedSfsCard: boolean,
) {
  const isCardBlocked = getIsCardBlocked(card.activation_status);
  const isCardsAccessManuallyRestricted =
    cardsAccess?.hasAccess === false && cardsAccess.initiator === 'user';

  const isTopUpButtonDisplayed =
    cardsAccess && (!isCardBlocked || isCardsAccessManuallyRestricted);
  const isTopUpButtonDisabled =
    isCardBeingBlockedOrUnblocked ||
    isCardBlocked ||
    isCardsAccessManuallyRestricted ||
    isNonActivatedSfsCard;
  return {
    isTopUpButtonDisplayed,
    isTopUpButtonDisabled,
  };
}

function computeButtonStates(
  card: ApiCard,
  cardsAccess: CardBlockingReasonAccess,
  isCardBeingBlockedOrUnblocked: boolean,
  isPinBeingSentBySms: boolean,
) {
  const isNonActivatedSfsCard =
    card.banking_provider === 'sfs' && card.status === 'PRE';

  const isCardBlocked = getIsCardBlocked(card.activation_status);

  const isCardsAccessManuallyRestricted =
    cardsAccess?.hasAccess === false && cardsAccess.initiator === 'user';

  const showSMSButtonForNonSFSBankingProvider = card.banking_provider !== 'sfs';
  const isSendSmsButtonDisplayed =
    showSMSButtonForNonSFSBankingProvider &&
    cardsAccess &&
    (!isCardBlocked || isCardsAccessManuallyRestricted);

  const isBlockUnblockButtonDisplayed = cardsAccess.hasAccess;
  const isCardReportButtonDisplayed =
    cardsAccess.hasAccess && isCardBlocked && !isCardsAccessManuallyRestricted;

  const isSendSmsButtonDisabled =
    isCardBeingBlockedOrUnblocked ||
    isPinBeingSentBySms ||
    isCardBlocked ||
    isCardsAccessManuallyRestricted ||
    isNonActivatedSfsCard;
  return {
    isCardBlocked,
    isSendSmsButtonDisplayed,
    isBlockUnblockButtonDisplayed,
    isCardReportButtonDisplayed,
    isSendSmsButtonDisabled,
    isNonActivatedSfsCard,
  };
}

export const PlasticCardActions = ({
  card,
  syncActivity,
  isCardBeingBlockedOrUnblocked,
  setIsCardBeingBlockedOrUnblocked,
}: Props) => {
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();
  const location = useLocation();
  const history = useHistory();

  const company = useCompany();
  const user = useUser();

  const [isPinBeingSentBySms, setIsPinBeingSentBySms] = useState(false);
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [isMarqetaPinWidgetModalOpen, setIsMarqetaPinWidgetModalOpen] =
    useState(false);

  const cardsAccess = getCardAccess(user.id, card.blocking_reason);

  const {
    isCardBlocked,
    isSendSmsButtonDisplayed,
    isBlockUnblockButtonDisplayed,
    isCardReportButtonDisplayed,
    isSendSmsButtonDisabled,
    isNonActivatedSfsCard,
  } = computeButtonStates(
    card,
    cardsAccess,
    isCardBeingBlockedOrUnblocked,
    isPinBeingSentBySms,
  );

  useEffect(() => {
    const { isTopUpButtonDisplayed, isTopUpButtonDisabled } =
      computeTopUpButtonState(
        card,
        cardsAccess,
        isCardBeingBlockedOrUnblocked,
        isNonActivatedSfsCard,
      );
    const params = new URLSearchParams(location.search);
    if (
      isTopUpModalOpen === false &&
      isTopUpButtonDisplayed &&
      !isTopUpButtonDisabled &&
      params.has(TOPUP_MODAL_QUERY)
    ) {
      setIsTopUpModalOpen(true);
    }
  }, []);

  const { isTopUpButtonDisplayed, isTopUpButtonDisabled } =
    computeTopUpButtonState(
      card,
      cardsAccess,
      isCardBeingBlockedOrUnblocked,
      isNonActivatedSfsCard,
    );

  const showMarqetaPinWidget = () => {
    track(AnalyticEventName.CARD_ACCESS_PIN, {});
    setIsMarqetaPinWidgetModalOpen(true);
  };

  const updateBlockedUnblockedStatus = async () => {
    await syncActivity();
    setIsCardBeingBlockedOrUnblocked(false);
    closeTopUpModal();
  };

  const closeTopUpModal = () => {
    setIsTopUpModalOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete(TOPUP_MODAL_QUERY);
    history.replace({
      search: params.toString(),
    });
  };

  const openTopUpModal = () => {
    setIsTopUpModalOpen(true);
    const params = new URLSearchParams(location.search);
    params.append(TOPUP_MODAL_QUERY, 'open');
    history.replace({
      search: params.toString(),
    });
  };

  const renderModals = () => {
    if (isTopUpModalOpen) {
      return (
        <TopUpModal
          card={card}
          mode="requester"
          onProcessed={async () => {
            await syncActivity();
            closeTopUpModal();
          }}
          onCancel={() => closeTopUpModal()}
        />
      );
    }
    if (isMarqetaPinWidgetModalOpen) {
      return (
        <MarqetaSetPinModal
          cardId={card.id}
          companyId={company.id}
          onClose={async () => {
            await syncActivity();
            setIsMarqetaPinWidgetModalOpen(false);
          }}
        />
      );
    }

    return null;
  };

  const getPinBySms = () => {
    if (!isPinBeingSentBySms) {
      track(AnalyticEventName.CARD_ACCESS_PIN, {});

      setIsPinBeingSentBySms(true);

      doCardAction(company.id, card.id, 'pinsms', null, async (error, data) => {
        if (data) {
          await syncActivity();
          successNotif(t('cards.successPinSentByText'));
          // @ts-expect-error: Not an helpful comment
        } else if (error && error === 'missing_phone') {
          dangerNotif(t('cards.errorPinMissingPhone'));
        } else {
          dangerNotif(t('errors:somethingWrong'));
        }
        setIsPinBeingSentBySms(false);
      });
    }
  };

  const columCount =
    (isBlockUnblockButtonDisplayed ? 1 : 0) +
    (isCardReportButtonDisplayed ? 1 : 0) +
    (isTopUpButtonDisplayed ? 1 : 0) +
    (isSendSmsButtonDisplayed ? 1 : 0) +
    (!isCardBlocked && card.banking_provider === 'sfs' ? 1 : 0);

  if (card.deleted_at) {
    return null;
  }

  return (
    <div className="mt-24 flex min-w-[320px] flex-col items-center justify-center">
      <div
        className="grid w-full place-items-center items-start"
        style={{ gridTemplateColumns: `repeat(${columCount}, 1fr)` }}
      >
        {isTopUpButtonDisplayed && (
          <IconButtonText
            isDisabled={isTopUpButtonDisabled}
            onClick={openTopUpModal}
            text={t('cards.actions.topUpLimit')}
            iconName="card-arrow-up"
          />
        )}
        {isSendSmsButtonDisplayed && (
          <Tooltip
            content={t('cards.disabledActionBlockedCardTip')}
            isDisabled={!isCardBlocked}
          >
            <IconButtonText
              isDisabled={isSendSmsButtonDisabled || isPinBeingSentBySms}
              onClick={
                card.banking_provider === 'bankable'
                  ? getPinBySms
                  : showMarqetaPinWidget
              }
              text={
                // eslint-disable-next-line no-nested-ternary
                isPinBeingSentBySms
                  ? t('cards.loadingPinCode')
                  : // eslint-disable-next-line unicorn/no-nested-ternary
                    card.banking_provider === 'bankable'
                    ? t('cards.actions.sendPinCode')
                    : t('cards.actions.resetPinCode')
              }
              iconName="key"
            />
          </Tooltip>
        )}
        <SFSRevealPinButton
          isBlocked={isCardBlocked}
          isDisabled={isCardBeingBlockedOrUnblocked}
          cardStatus={card.status}
        />

        {isBlockUnblockButtonDisplayed && (
          <BlockUnblock
            card={card}
            isCardBlocked={isCardBlocked}
            isCardBeingBlockedOrUnblocked={isCardBeingBlockedOrUnblocked}
            setIsCardBeingBlockedOrUnblocked={setIsCardBeingBlockedOrUnblocked}
            onProcessed={updateBlockedUnblockedStatus}
            smallButton
          />
        )}
        {isCardReportButtonDisplayed && (
          <CardReport
            card={card}
            isDisabled={isCardBeingBlockedOrUnblocked}
            onCardReported={afterReportingCard}
          />
        )}
      </div>
      <SfsUkRevealPaymentPasswordButton
        isBlocked={isCardBlocked}
        cardStatus={card.status}
        isDisabled={isCardBeingBlockedOrUnblocked}
      />
      {renderModals()}
    </div>
  );
};

const afterReportingCard = () => {
  // Redirect towards the app root url, as the card do not exist anymore,
  // the current page access being now useless / dangerous
  setTimeout(() => {
    window.location.href = `${window.location.origin}/app`;
  }, 1500);
};
